import { $, esc, isDesktop, footerHtml } from "../core/dom.js";
import { db, rpc } from "../core/supabaseClient.js";
import { state } from "../core/state.js";
import { fetchShow } from "../core/leagueShows.js";
import { CARTON_SITE_BASE } from "../core/config.js";
import { fmtDate, fmtCutoff, countdown, clearTimers, clearTimersFor, showState } from "../core/format.js";
import { trophy, winBadge, laurelSpray } from "../core/trophy.js";
import { toast } from "../core/toast.js";
import { currentBracket } from "../core/switcher.js";
import { SLOT_LABELS, SLOT_TOOLTIPS, FLAT_PICK_TOOLTIP, slotLabelFor } from "../core/slotTypes.js";

// Case/whitespace-insensitive song-name comparison — the catalog
// (songs_cache, synced from The Carton) has real entries with stray
// leading/trailing whitespace (confirmed: 7 of 363 rows as of this
// writing, e.g. "Time Escaping " with a trailing space). Autocomplete's
// substring filter tolerated that silently (`.includes()` doesn't care
// about extra trailing characters on the catalog side), but the save-time
// catalog check compared a *trimmed* input value against the catalog's
// *untrimmed* string with strict equality — same song, real mismatch,
// false "not in catalog" warning. Every songname comparison in this file
// routes through this one function now so the two checks can't drift
// apart again.
export const normSong = v => (v||"").trim().toLowerCase();
export const isWildcard = v => normSong(v) === "any debut";

// An open-padlock emoji (🔓) technically IS the "unlocked" codepoint, but at
// the ~14px this renders inline next to a slot it reads as just "a padlock"
// — the open-shackle detail that actually conveys "unlocked" disappears at
// that size (confirmed by rendering both the locked/unlocked emoji at real
// size side by side). A line icon with the shackle swung open and clearly
// disconnected from the body holds up at small size in a way the emoji
// didn't, and inherits `color` instead of depending on whatever emoji font
// the viewer's device happens to render with.
const UNLOCKED_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>';

// One player's drafts in one bracket share this prefix; the shows list's
// welcome-card derivation scans it for either of the league's brackets.
export function draftPrefix(bracketId){ return `ft_draft_${state.session.id}_${bracketId}_`; }
export function draftKey(showId){ return draftPrefix(state.currentBracketId) + showId; }
// First-save toast flag (onboarding.md Batch 3, item 4): once per player per
// device. The shows list also sets it silently whenever it sees saved picks
// (approved decision 3), so only a genuinely new player gets the long toast.
export function firstSaveKey(){ return `ft_first_save_${state.session.id}`; }
const FIRST_SAVE_TOAST = "<b>Picks locked in ✔</b> Change them any time before cutoff. Scores show up on the show's Scores page after it ends.";

// Scores-page View chip (standings-ux.md 2a, LOCKED 2026-10-25): "compact"
// = every entry collapsed to name + points; "detail" = every entry open
// (pick rows with per-pick points). A per-device preference in
// localStorage, same posture as ft_theme2: read once at module load,
// written on toggle, never account state, never sent anywhere. Default
// compact. Only the two known values are honoured. The toggle re-renders
// from the last fetched data (lastDetail) rather than refetching three
// RPCs on venue LTE; the cache is keyed by show id so a stale one is never
// rendered for a different show.
const SCORES_VIEWS = ["compact", "detail"];
let scoresView = SCORES_VIEWS.includes(localStorage.getItem("ft_scores_view")) ? localStorage.getItem("ft_scores_view") : "compact";
let lastDetail = null;
export function setScoresView(v){
  if (!SCORES_VIEWS.includes(v)) return;
  scoresView = v;
  localStorage.setItem("ft_scores_view", v);
  if (lastDetail && state.currentShow && lastDetail.show.id === state.currentShow.id) renderShowDetailHtml(lastDetail);
}

export async function openShow(id){
  if (isDesktop()) state.tab = "shows";
  clearTimersFor("shows");
  const show = await fetchShow(id);
  state.currentShow = show;
  const st = showState(show);
  if (st !== "open"){ renderShowDetail(show); return; }
  // Official gating is authoritative server-side (submit_picks calls the
  // same _official_gate helper) — this call is purely so a player isn't
  // shown a form they can't submit. Casual never gates (RPC short-circuits
  // ok=true), so this always resolves fast for the common case.
  let gate = { ok: true, reason: null };
  try{
    const [row] = await rpc("can_submit_picks", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id:show.id });
    if (row) gate = row;
  }catch(e){ /* fail open to the sheet — submit_picks itself still enforces this */ }
  if (gate.ok) renderPickSheet(show);
  else renderIneligible(show, gate.reason);
}

function renderIneligible(show, reason){
  const casual = state.leagues.find(l => l.league_id === state.currentLeagueId && l.bracket_kind === "casual");
  $("#main").innerHTML = `
    <p style="margin-top:14px"><button class="btn ghost small" onclick="renderShows()">← shows</button></p>
    <div class="sheet">
      <h2>${esc(show.venue||"TBA")}</h2>
      <div class="sub">${fmtDate(show.showdate)}</div>
      <p class="ineligible-reason">${esc(reason || "Picks aren't open for this bracket.")}</p>
      ${casual ? `<button class="btn ghost small" onclick="switchToBracket(${casual.bracket_id})">Switch to Casual</button>` : ""}
    </div>
    ${footerHtml()}`;
}

function prettifySlotKey(key){
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// Canonical pick-sheet order + display label for every slot in a show's config,
// for rendering a frozen breakdown consistently regardless of DB row order.
// `order`: configured slots in configured order, then flat picks numerically.
// Any breakdown slot key missing from `order` (legacy/removed slot) sorts last
// and falls back to a prettified key rather than the raw key. Label comes
// from the fixed slotLabelFor() map, not the (now unused, possibly still
// sitting in old config JSON) admin-typed `s.label` — a slot whose TYPE
// isn't in that map at all (a genuinely retired type) prettifies its key
// too, same fallback as the missing-from-order case just above it.
function breakdownSlotInfo(format){
  // Ranked mode returns before the slot logic, same as slotDefs and for the
  // same reason — everything below reads `oneset`/`flat_picks`, which a
  // ranked bracket still carries but never uses.
  //
  // This branch is NOT cosmetic, which is easy to assume since the fallback
  // labels ranked rows correctly on its own: `prettifySlotKey` turns
  // "rank1" into "Rank 1" without help. What the fallback cannot do is
  // ORDER them. A key missing from `order` compares equal to every other
  // missing key in sortBySlotOrder (both indexOf calls return -1, so it
  // returns 0), leaving Rank 1..N in whatever order the DB happened to
  // return the rows — which will often look right by luck and occasionally
  // won't. Supplying a real order is what makes the frozen breakdown
  // display in rank order deterministically.
  if (state.cfg.mode === "ranked_choice"){
    const ladder = state.cfg.ranked?.ladder ?? [];
    const order = ladder.map((_, i) => "rank" + (i+1));
    const label = {};
    order.forEach((k, i) => { label[k] = "Rank " + (i+1); });
    return { order, label };
  }
  const sect = (format === "one_set" && state.cfg.oneset) ? state.cfg.oneset : state.cfg;
  const slots = sect.slots || [];
  const coverKeys = slots.filter(s => (s.type||s.key) === "cover_pick").map(s => s.key);
  const order = [], label = {};
  slots.forEach(s => {
    order.push(s.key);
    const base = slotLabelFor(s.type||s.key, format) || prettifySlotKey(s.type||s.key);
    label[s.key] = (coverKeys.length > 1 && coverKeys.includes(s.key))
      ? `${base} ${coverKeys.indexOf(s.key)+1}`
      : base;
  });
  for (let i=1; i<=(sect.flat_picks||0); i++){ order.push("flat"+i); label["flat"+i] = "Pick "+i; }
  return { order, label };
}

function sortBySlotOrder(items, order){
  return [...items].sort((a,b) => {
    const ia = order.indexOf(a.slot), ib = order.indexOf(b.slot);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

export function slotDefs(format){
  // Ranked choice has no positional slots at all, so it returns before any
  // of the slot/flat logic below runs. That ordering is load-bearing rather
  // than tidy: everything past this point reads `oneset`/`flat_picks`, and
  // a ranked bracket's config still carries both (admin.js's read-through
  // save guards preserve them deliberately, so a mode round-trip can't
  // destroy an admin's slot setup). Returning first is what stops those
  // values reaching a mode that has neither.
  //
  // Row count is fixed by the ladder and never varies by show format —
  // `cfg.ranked` is read from the top level, never through the `oneset`
  // branch, so a one-set show and a standard show render the same sheet.
  // No tooltip per row: "The Rules" card explains the ladder once instead
  // of repeating the same sentence N times (see renderPickSheet).
  if (state.cfg.mode === "ranked_choice")
    return (state.cfg.ranked?.ladder ?? []).map((pts, i) =>
      ({ key: "rank" + (i+1), label: "Rank " + (i+1), tooltip: null, pts, type: "ranked" }));
  const sect = (format === "one_set" && state.cfg.oneset) ? state.cfg.oneset : state.cfg;
  const slots = (sect.slots||[]).map(s => {
    const type = s.type||s.key;
    return { key:s.key, label: slotLabelFor(type, format) || prettifySlotKey(type), tooltip: SLOT_TOOLTIPS[type] || null, pts:s.points, type };
  });
  for (let i=1; i<=(sect.flat_picks||0); i++) slots.push({ key:"flat"+i, label:"Pick "+i, tooltip: FLAT_PICK_TOOLTIP, pts:sect.flat_points ?? state.cfg.flat_points ?? 1, flat:true });
  return slots;
}

export async function renderPickSheet(show){
  let mine = [];
  try{ mine = await rpc("get_my_picks", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id:show.id }); }catch(e){}
  const dKey = draftKey(show.id);
  const draft = JSON.parse(localStorage.getItem(dKey) || "null");
  const savedVal = k => ((mine.find(p => p.slot===k)||{}).songname || "").trim();
  const val = k => esc((draft && draft[k] != null ? draft[k] : savedVal(k)) || "");
  const slots = slotDefs(show.format);
  // Empty-ladder guard (admin-panel.md → "EMPTY RANKED LADDER", LOCKED
  // 2026-10-26, decision 3): ranked mode with no ladder has no rows to
  // draw. A bare "Lock 'em in" over nothing is what shipped on 2026-10-26
  // (docs/history/2026-10-26-empty-ranked-ladder.md); say what is wrong
  // and who can fix it. The ROLE, not a nickname — the admin's name is
  // not readable with the anon key, and nicknames stay off tracked files.
  // Paper-safe markup only (.sheet is theme-invariant — frontend-css.md),
  // and no save/revert/countdown wiring, since none of those elements
  // exist on this sheet.
  if (state.cfg.mode === "ranked_choice" && !slots.length){
    const note = "This bracket's ranked ladder isn't set up yet, so there's nothing to pick. Ask your league admin to save the ladder under Game rules.";
    $("#main").innerHTML = `
    <p style="margin-top:14px"><button class="btn ghost small" onclick="renderShows()">← shows</button></p>
    <div class="sheet">
      <h2>${esc(show.venue||"TBA")}</h2>
      <div class="sub">${fmtDate(show.showdate)} · ${esc(show.city||"")}${show.state?", "+esc(show.state):""}${show.format==='one_set' ? " · FESTIVAL SET" : ""}</div>
      <p class="sheetnote" id="ladder-missing">${note}</p>
    </div>
    <div class="sheet rules-sheet">
      <h2>The Rules</h2>
      <p class="sheetnote">${note}</p>
    </div>
    ${footerHtml()}`;
    return;
  }
  // Ranked rows drop the label and put the points bubble where it was: the
  // rank IS the points, so "Rank 1" and "5" are two encodings of one fact at
  // opposite ends of the row, and the number is the one that tells a player
  // something. Slot mode keeps its labels — "Set 2 Closer" is real
  // information the points don't convey.
  //
  // The label is OMITTED rather than hidden with CSS. Hiding it would keep
  // the markup uniform, but would leave "Rank N" in the DOM as text nothing
  // displays — and the scenario asserts that text, so we'd have a passing
  // check about something invisible. The modifier class carries only the
  // reordering (.slotline.ranked .pts{order:-1} in styles.css).
  //
  // Note slotDefs still RETURNS label:"Rank N" — only this row omits it.
  // renderShowDetail's pre-scoring pick board reads those same labels, and
  // there the rank is meaningful, same as in the scored breakdown.
  const rankedRow = state.cfg.mode === "ranked_choice";
  const slotHtml = s => `
    <div class="slotline autocomplete${rankedRow ? " ranked" : ""}">
      ${rankedRow ? "" : `<label>${esc(s.label)}</label>`}
      <input data-slot="${s.key}" data-type="${s.type||s.key}" value="${val(s.key)}" placeholder="${(s.type||s.key)==="cover_pick"?"a cover…":"song…"}" autocomplete="off" spellcheck="false">
      <span class="pts">${s.pts}</span>
      <span class="unsaved" title="Unsaved change — differs from your saved pick">${UNLOCKED_ICON}</span>
    </div>`;
  const structured = slots.filter(s=>!s.flat), flats = slots.filter(s=>s.flat);
  // "The Rules" companion card: one definition per distinct label (a
  // second Cover Pick slot, or several flat picks, share one row rather
  // than repeating the same tooltip text) — auto-generated from whatever
  // slots this bracket's config actually has active, so it can't drift
  // out of sync with the sheet above it the way admin-typed free text
  // could. Custom rules are bracket-wide (admin.js's readCustomRules(),
  // stored on brackets.config directly, not per-format like slots) — no
  // "House rules" divider at all when the admin hasn't written any,
  // rather than an empty header.
  const ruleDefs = (() => {
    const perfectPts = Number((state.cfg.bonuses ?? {}).perfect ?? 0);
    // Perfect-sheet row, appended to whichever mode's list below. Two
    // conditions, both load-bearing:
    //   - `perfectPts > 0` — a "Perfect: +0" row is worse than no
    //     row, since it advertises a bonus that cannot be earned.
    //   - `defs.length` — a bracket with no ladder/slots configured renders
    //     no pick rows at all, so a lone bonus row would describe a sheet
    //     that isn't on screen.
    // The value is read from config rather than written into the string,
    // so an admin changing the bonus can't leave the copy claiming the old
    // number.
    const withPerfect = (defs, desc) =>
      (defs.length && perfectPts > 0) ? [...defs, { term: "Perfect", desc }] : defs;

    // Ranked mode gets ONE ladder row, not one per rank. The dedup below is
    // by label, and every rank has a distinct one ("Rank 1", "Rank 2", ...),
    // so left alone this would render N rows repeating the same sentence
    // with a different number — the opposite of what the dedup exists for.
    // What a player actually needs explained is the ladder, once.
    if (state.cfg.mode === "ranked_choice"){
      const ladder = state.cfg.ranked?.ladder ?? [];
      if (!ladder.length) return [];
      // Worded against what the player can actually SEE. The sheet's rows
      // are no longer labelled "Rank N" (see slotHtml), so naming ranks
      // here would point at something off-screen. The number beside each
      // row is the whole vocabulary now — which is also why this no longer
      // spells out the ladder's top and bottom values: they're printed
      // beside the rows, and repeating them here cost four wrapped lines
      // on a phone to restate what's already visible.
      return withPerfect([{
        term: "Ladder",
        desc: "Each row scores the number beside it if that song is played, anywhere in the show.",
      }], `Fill all ${ladder.length} rows and have every song played: +${perfectPts}.`);
    }
    const seen = new Set();
    const defs = structured.filter(s => !seen.has(s.label) && seen.add(s.label)).map(s => ({ term: s.label, desc: s.tooltip }));
    if (flats.length) defs.push({ term: flats.length > 1 ? `Pick 1–${flats.length}` : "Pick", desc: FLAT_PICK_TOOLTIP });
    // "slots don't have to match" is not padding — slot mode's perfect
    // bonus fires on `breakdown.every(x => x.hit)`, and a played-but-
    // wrong-slot pick sets hit=true. So the bonus really does pay out on a
    // sheet where every song played and every slot was wrong. Players
    // assume the opposite, and would otherwise only learn it by losing to
    // it.
    return withPerfect(defs, `Fill every row and have every song played — slots don't have to match: +${perfectPts}.`);
  })();
  const customRules = state.cfg.custom_rules || [];
  $("#main").innerHTML = `
    <p style="margin-top:14px"><button class="btn ghost small" onclick="renderShows()">← shows</button></p>
    <div class="sheet">
      <h2>${esc(show.venue||"TBA")}</h2>
      <div class="sub">${fmtDate(show.showdate)} · ${esc(show.city||"")}${show.state?", "+esc(show.state):""}${show.format==='one_set' ? " · FESTIVAL SET" : ""}</div>
      <button class="revertlink" id="revert-link">Revert to saved</button>
      ${structured.map(slotHtml).join("")}
      ${flats.length ? `<div class="divider">Anywhere in the show</div>${flats.map(slotHtml).join("")}` : ""}
      <button class="savebtn" id="save">Lock 'em in</button>
      <p style="font-size:.75rem;margin:8px 0 0;text-align:center;color:var(--paper-ink-soft)">Change your picks any time until cutoff.</p>
      <div class="countbig">${state.cfg.voting_override==='open' ? 'Admin override — voting open' : `Cutoff ${fmtCutoff(show.cutoff_at)} · <b id="cd"></b>`}</div>
      <div class="err" id="p-err" style="text-align:center"></div>
      ${currentBracket()?.bracket_kind === "official" ? laurelSpray() : ""}
    </div>
    <div class="sheet rules-sheet">
      <h2>The Rules</h2>
      <div class="ruledefs">
        ${ruleDefs.map(d => `<div class="ruledef"><span class="rd-term">${esc(d.term)}</span><span class="rd-desc">${esc(d.desc||"")}</span></div>`).join("")}
      </div>
      ${customRules.length ? `<div class="divider">House rules</div><ul class="customrules">${customRules.map(r => `<li>${esc(r)}</li>`).join("")}</ul>` : ""}
      ${state.cfg.mode === "ranked_choice" ? ""
        : `<p class="rulenote">Numbers on the pick sheet are points per slot.</p>`}
    </div>
    ${footerHtml()}`;
  document.querySelectorAll(".slotline input").forEach(attachAutocomplete);
  const sheetEl = $("#main").querySelector(".sheet");
  // Recomputed after every keystroke — the FULL current sheet snapshot is
  // what gets persisted (not just non-empty slots, the old behavior), so
  // that clearing a previously-saved slot back to empty is itself a
  // recorded change rather than a dropped key that silently un-clears on
  // reload. When nothing in the sheet actually differs from what's saved —
  // including the common "edited a slot, then undid it" case — the draft
  // is removed entirely rather than left as a no-op key, so no marker
  // survives on the sheet or (via the show row's own hasDraft check) on
  // the shows list either. Also run once immediately after render, so a
  // stale draft from a previous visit is judged right away instead of
  // waiting for the next keystroke.
  const syncDirty = () => {
    const snapshot = {};
    let anyDirty = false;
    document.querySelectorAll(".slotline input").forEach(inp => {
      const dirty = inp.value.trim() !== savedVal(inp.dataset.slot);
      snapshot[inp.dataset.slot] = inp.value;
      inp.closest(".slotline").classList.toggle("dirty", dirty);
      if (dirty) anyDirty = true;
    });
    if (anyDirty) localStorage.setItem(dKey, JSON.stringify(snapshot));
    else localStorage.removeItem(dKey);
    sheetEl.classList.toggle("dirty", anyDirty);
  };
  document.querySelectorAll(".slotline input").forEach(inp => inp.addEventListener("input", syncDirty));
  syncDirty();
  $("#save").onclick = savePicks;
  $("#revert-link").onclick = () => {
    if (!confirm("Discard your unsaved changes and revert every slot to your last saved picks?")) return;
    localStorage.removeItem(dKey);
    openShow(show.id);
  };
  if (state.cfg.voting_override !== 'open' && show.cutoff_at) state.timers.push(setInterval(() => {
    const cd = countdown(show.cutoff_at);
    if (cd) $("#cd").textContent = cd;
    else { toast("All picks are locked — enjoy the show 🥚"); openShow(show.id); }
  }, 1000));
}

export function attachAutocomplete(input){
  let list = null, sel = -1;
  const close = () => { list?.remove(); list = null; sel = -1; };
  input.addEventListener("input", () => {
    close();
    const q = normSong(input.value);
    if (q.length < 1) return;
    const coverOnly = input.dataset.type === "cover_pick";
    const pool = coverOnly ? state.songList.filter(s => s.is_original === false) : state.songList;
    const wc = [];
    // The mode check is not redundant with the wildcards.debut flag, and
    // leaving it out was a real bug: a ranked bracket PRESERVES that flag
    // (admin.js's read-through save guards keep it so a mode round-trip
    // can't destroy the admin's setting), while scoreRankedPicks ignores
    // the wildcard entirely and scores "Any Debut" as an ordinary
    // unmatched song name — i.e. 0. Without this the sheet would suggest a
    // starred pick that silently scores nothing. See savePicks for the
    // other half of the same bug.
    const wildcardOffered = state.cfg.mode !== "ranked_choice" && (state.cfg.wildcards?.debut ?? true);
    if (!coverOnly && wildcardOffered && ("any debut".includes(q) || "debut".includes(q)))
      wc.push({ songname: "Any Debut", times_played: "★" });
    const hits = [...wc, ...pool.filter(s => normSong(s.songname).includes(q))].slice(0, 8);
    if (!hits.length) return;
    list = document.createElement("div"); list.className = "acc-list";
    hits.forEach(h => {
      const d = document.createElement("div");
      d.innerHTML = `${esc(h.songname)} <small>${h.times_played ?? "–"}×</small>`;
      d.onmousedown = e => { e.preventDefault(); input.value = h.songname; input.dispatchEvent(new Event("input")); close(); };
      list.appendChild(d);
    });
    input.parentElement.appendChild(list);
  });
  input.addEventListener("keydown", e => {
    if (!list) return;
    const items = [...list.children];
    if (e.key === "ArrowDown"){ sel = Math.min(sel+1, items.length-1); }
    else if (e.key === "ArrowUp"){ sel = Math.max(sel-1, 0); }
    else if (e.key === "Enter"){ e.preventDefault(); if (sel>=0){ input.value = items[sel].textContent.replace(/\s*\S*×$/,"").trim(); input.dispatchEvent(new Event("input")); } close(); return; }
    else return;
    items.forEach((it,i) => it.classList.toggle("sel", i===sel));
  });
  input.addEventListener("blur", () => setTimeout(close, 150));
}

export async function savePicks(){
  $("#p-err").textContent = "";
  const picks = [...document.querySelectorAll(".slotline input")]
    .map(i => ({ slot: i.dataset.slot, songname: i.value.trim() }))
    .filter(p => p.songname);
  // warn on unknown songs (typos) but allow — could be a debut call
  // The wildcard exemption is mode-scoped for the same reason the
  // autocomplete gate is: in ranked mode "Any Debut" is not a wildcard, it
  // is just a song name that matches nothing and scores 0. Exempting it
  // there would let a typed-from-memory "Any Debut" save silently, skipping
  // the not-in-catalog confirm that is the last thing standing between the
  // player and a dead pick.
  const wildcardLive = state.cfg.mode !== "ranked_choice";
  const unknown = picks.filter(p => !(wildcardLive && isWildcard(p.songname)) && !state.songList.some(s => normSong(s.songname) === normSong(p.songname)));
  if (unknown.length && !confirm(`Not in the catalog (typo, or a bold debut call?):\n${unknown.map(u=>u.songname).join("\n")}\n\nSave anyway?`)) return;
  try{
    await rpc("submit_picks", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id:state.currentShow.id, p_picks:picks });
    localStorage.removeItem(draftKey(state.currentShow.id));
    let first = false;
    try { first = !localStorage.getItem(firstSaveKey()); localStorage.setItem(firstSaveKey(), "1"); } catch { first = false; }
    if (first) toast(FIRST_SAVE_TOAST, "score", null, 10000);
    else toast("Picks saved ✔", "score");
    // Re-open rather than leave the just-saved DOM in place — the sheet's
    // dirty markers/revert link were computed against the pre-save `mine`,
    // which is now stale (server has these exact values). openShow() clears
    // its own timer before re-registering one (see its clearTimersFor call),
    // same self-reopen already used by the cutoff countdown below, so this
    // isn't a new call pattern.
    openShow(state.currentShow.id);
  }catch(e){ $("#p-err").textContent = e.message; }
}

// Visible failure screen for renderShowDetail. Deliberately a `.panel`, NOT
// the `.sheet` paper card renderIneligible uses: --coral on the cream paper
// fails WCAG AA in BOTH themes (2.54:1 dark / 3.70:1 light — see the
// Frontend/CSS gotchas in CLAUDE.md, still unfixed), and an error nobody can
// read is the same bug this function exists to fix. On an app panel, coral
// is the normal, tested treatment.
function renderShowDetailError(show, err){
  $("#main").innerHTML = `
    <p style="margin-top:14px"><button class="btn ghost small" onclick="renderShows()">← shows</button></p>
    <div class="panel" style="border-color:var(--coral)">
      <h2>Couldn't load this show</h2>
      <p class="muted" style="word-break:break-word">${esc(err && err.message ? err.message : String(err))}</p>
      <div class="row" style="margin-top:10px">
        <button class="btn" onclick="openShow(${show.id})">Try again</button>
      </div>
    </div>
    ${footerHtml()}`;
}

export async function renderShowDetail(show){
  clearTimers();
  let setlist, picks, scores;
  // The old code let get_show_picks fail silently via `.catch(() => [])`,
  // which rendered an empty pick board — indistinguishable from "nobody
  // picked." Removing that catch alone was NOT enough, and shipping it that
  // way would have been a regression: openShow() is called from inline
  // onclick handlers and never awaits or catches this function, so a bare
  // rejection is an unhandled promise rejection and the screen simply never
  // changes — a dead button with no message at all, strictly less visible
  // than the empty board it replaced. Hence the explicit try/catch and a
  // real error screen: a member who legitimately can't read picks (the
  // membership gate added in stage_p) now sees WHY, and everything else
  // that can fail here surfaces the same way instead of vanishing.
  try{
    const [setlistRes, picksRes, scoresRes] = await Promise.all([
      db.from("setlist_songs").select("*").eq("show_id", show.id).order("position"),
      rpc("get_show_picks", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id: state.currentBracketId, p_show_id: show.id }),
      rpc("get_bracket_scores", { p_name:state.session.name, p_pin:state.session.pin, p_bracket_id:state.currentBracketId, p_show_id: show.id })
        .then(rows => (rows||[]).sort((a,b) => b.points - a.points)),
    ]);
    setlist = setlistRes.data; picks = picksRes; scores = scoresRes;
  }catch(e){
    renderShowDetailError(show, e);
    return;
  }
  lastDetail = { show, setlist, picks, scores };
  renderShowDetailHtml(lastDetail);
}
// Pure render from already-fetched data — split from renderShowDetail so
// the View chip (setScoresView) can re-render without a refetch.
function renderShowDetailHtml({ show, setlist, picks, scores }){
  const pname = Object.fromEntries((scores||[]).map(s => [s.player_id, s.player_name]));
  // is_mine is computed server-side now (stage_p) — the RPC no longer
  // returns player_id at all, so there is no client-side uuid comparison to
  // make. Every other player's uuid simply isn't in the payload any more.
  const mineHits = new Set((picks||[]).filter(p => p.is_mine).map(p => p.songname.toLowerCase()));
  let lastSet = null;
  const setHtml = (setlist||[]).map(s => {
    const label = s.is_encore ? "Encore" : "Set " + (s.setnumber || "1");
    const brk = label !== lastSet ? `<div class="setbreak">${esc(label)}</div>` : "";
    lastSet = label;
    // Debut detection is a footnote regex match (same /debut/i test scoring.js
    // and index.ts both use), not a structured flag — it inherits whatever the
    // tapers wrote, so a missed or misworded footnote means a missed debut
    // here same as in the wildcard and the bonus.
    const isDebut = /debut/i.test(s.footnote || "");
    return brk + `
    <div class="songrow ${mineHits.has(s.songname.toLowerCase()) ? "hitmine" : ""}">
      <span class="pos">${s.position}</span>
      <span class="name">${esc(s.songname)}${s.segue ? ' <span class="segue">&gt;</span>' : ""}${isDebut ? ' <span class="debut">DEBUT 🥚</span>' : ""}</span>
    </div>`;
  }).join("");
  const attribution = (setlist||[]).length ? `<p class="muted" style="text-align:center">Setlist data from ${
    show.permalink ? `<a href="${CARTON_SITE_BASE}/${esc(show.permalink)}" target="_blank" rel="noopener">The Carton</a>` : "The Carton"
  }.</p>` : "";
  const { order: brkOrder, label: brkLabel } = breakdownSlotInfo(show.format);
  // Only surface the explainer when it's actually relevant — a closer-family
  // pick is currently sitting on provisional off-slot points because the
  // encore (or the show) hasn't confirmed it yet. Keeps the note out of the
  // way once nothing's ambiguous, so it doesn't read as boilerplate.
  const hasUndetermined = (scores||[]).some(sc => (sc.breakdown||[]).some(b => (b.reason||"").includes("slot undetermined")));
  // One card per scoring player. `.mine` marks the viewer's own entry for
  // the yolk outline (standings-ux.md 3a): player_id against the
  // server-issued session id — the same mechanism as .me on the board;
  // get_bracket_scores carries player_id, get_show_picks does not. Point
  // colors are untouched. Pick rows render only in Detail (2a); Compact is
  // true DOM absence, not a hidden block. Name in .nm + title (4).
  const scoreHtml = (scores||[]).map(sc => `
    <div class="panel scoreentry${sc.player_id === state.session.id ? " mine" : ""}" style="padding:12px">
      <div class="row"><b class="nm" title="${esc(pname[sc.player_id] || "?")}">${esc(pname[sc.player_id] || "?")}</b>
        <span class="pts" style="margin-left:auto;font-family:var(--mono);color:var(--yolk)">${sc.points} pts</span></div>
      ${scoresView !== "detail" ? "" : sortBySlotOrder(sc.breakdown||[], brkOrder).map(b => `
        <div class="pickres ${b.points>0?"hit":b.hit?"":"miss"}">
          <span class="sl">${esc(brkLabel[b.slot] || prettifySlotKey(b.slot))}</span><span>${esc(b.songname)}</span>
          <span class="pt">${b.points>0?"+"+b.points:"·"} <small class="muted">${esc(b.reason)}</small></span>
        </div>`).join("")}
    </div>`).join("");
  const viewChip = `<div class="chiprow scorehead" id="scoresview"><h2>Score</h2><span class="lbl">View</span><div class="switcher">
        <button class="linkbtn switcher-btn${scoresView==="compact"?" on":""}" onclick="setScoresView('compact')">Compact</button>
        <button class="linkbtn switcher-btn${scoresView==="detail"?" on":""}" onclick="setScoresView('detail')">Detail</button></div></div>`;
  // pre-scoring pick board: cutoff has passed, picks are public, but no scores yet
  let pickBoard = "";
  if (!(scores||[]).length && (picks||[]).length){
    const slotOrder = slotDefs(show.format).map(sl => sl.key);
    const slotLabel = Object.fromEntries(slotDefs(show.format).map(sl => [sl.key, sl.label]));
    const byName = {};
    for (const p of picks) (byName[p.player_name] ??= []).push(p);
    pickBoard = `<h2 style="margin:18px 4px 4px">The picks are in</h2>` +
      Object.entries(byName).sort((a,b) => a[0].localeCompare(b[0])).map(([name, pp]) => `
        <div class="panel pickboard" style="padding:12px"><div class="row"><b class="nm" title="${esc(name)}">${esc(name)}</b></div>
          ${pp.sort((a,b) => slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot)).map(p => `
            <div class="pickres"><span class="sl">${esc(slotLabel[p.slot] || p.slot)}</span>
              <span>${esc(p.songname)}</span></div>`).join("")}
        </div>`).join("");
  }
  $("#main").innerHTML = `
    <p style="margin-top:14px"><button class="btn ghost small" onclick="renderShows()">← shows</button>
      <span class="pill ${showState(show)==='live'?'live':'final'}">${showState(show)==='final'?'complete':showState(show)}</span></p>
    ${(() => {
      if (show.status!=='final' || !(scores||[]).length) return "";
      const top = scores[0].points;
      if (top <= 0) return `<div class="panel"><h2>No winner</h2><p class="muted">Nobody scored on this one.</p></div>`;
      const champs = scores.filter(x => x.points === top).map(x => esc(pname[x.player_id]||"?"));
      return `<div class="panel" style="border-color:var(--yolk)">
        <h2>${winBadge(64)} ${champs.join(" & ")} ${champs.length>1?"tie":"takes it"}</h2>
        <p class="muted">${top} points${champs.length>1?" apiece":""}</p></div>`;
    })()}
    <div class="panel"><h2>${esc(show.venue||"")} <span class="muted" style="font-size:.85rem">${fmtDate(show.showdate)}</span></h2>
      ${currentBracket()?.bracket_kind === "official" ? `<div class="row" style="justify-content:center;gap:10px;margin:4px 0 12px">${trophy(26)}<span style="font-family:'Fraunces',serif;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--yolk);font-size:.85rem">Official</span>${trophy(26)}</div>` : ""}
      ${setHtml || '<p class="muted">No setlist yet. It shows up here song-by-song once the tapers get typing.</p>'}</div>${attribution}
    ${pickBoard}
    ${viewChip}
    ${hasUndetermined ? `<p class="muted" style="text-align:center;margin:0 4px 8px">Closer-type picks show off-slot points (if enabled) until the encore starts (or the show ends) — full points awarded once determined.</p>` : ""}
    ${scoreHtml || '<p class="muted" style="margin:8px 4px">No scores yet — they appear with the first song.</p>'}
    ${footerHtml()}`;
}
